/**
 * Which pose the dog is in (ROUND8 §3): a body motion with a head laid over it. dogGait.ts holds the poses;
 * this file chooses, and plays the one one-shot a dog has - going down. Like the soldier's, every pose is a pure
 * function of its input and the clock, which the caller has already stepped "on twos" (05 §6.2).
 */

import { type Bone, Quaternion, Vector3 } from 'three';
import {
  alertHead, barkHead, type DogBoneOf, deadDog, gait, resetDog, sit, sniffHead, stand,
} from './dogGait';
import { D, DOG_BONE_COUNT } from './dogRig';

export { BARK_OPEN_SECONDS } from './dogGait';

/** `death` plays from `age` = 0 and ends in `dead`, the corpse. */
export type DogMotion = 'stand' | 'walk' | 'trot' | 'sit' | 'death' | 'dead';

/**
 * `alert` - level, ears up. `sniff` - nose down and sweeping: casting for the trail, or running it.
 * `bark` - head thrown up and giving tongue: it has him. A corpse ignores it.
 */
export type DogHead = 'alert' | 'sniff' | 'bark';

export interface DogPoseInput {
  motion: DogMotion;
  head: DogHead;
  /** Metres travelled: drives the gait so paws do not slide. */
  travelled: number;
  /** Seconds since the death began (`death`). */
  age: number;
  /** Seconds since it last barked: the jaw snaps open at each one. Negative = never. */
  sinceBark: number;
}

/** Every motion and head, in the order the pose viewer and the tests walk them. */
export const DOG_MOTIONS: readonly DogMotion[] = ['stand', 'walk', 'trot', 'sit', 'death', 'dead'];
export const DOG_HEADS: readonly DogHead[] = ['alert', 'sniff', 'bark'];

/** How long going down takes: a stagger, the legs going, and over onto its side. */
export const DOG_DEATH_SECONDS = 0.6;
/** The stagger ends, and the legs fold, this far through the death. */
const BUCKLE_AT = 0.4;
/** How far the body rides up on its legs as it rolls onto its side, metres. */
const ROLL_LIFT = 0.08;

/* Scratch for the death's in-betweens: the per-frame path allocates nothing. */
const SNAP = Array.from({ length: DOG_BONE_COUNT }, () => ({ p: new Vector3(), q: new Quaternion() }));
const _q = new Quaternion();
const _p = new Vector3();

const smooth = (k: number): number => k * k * (3 - 2 * k);

/** Legs going, the body dropping and starting to roll: the middle drawing of the death. */
function buckle(bone: DogBoneOf): void {
  stand(bone);
  const body = bone(D.body);
  body.rotation.set(0.1, 0, 0.45);
  // Folded tight, so that rolling over them does not drive the paws into the ground.
  for (const i of [D.lFore, D.rFore, D.lHind, D.rHind]) bone(i).rotation.x = -0.9;
  for (const i of [D.lForeLow, D.rForeLow, D.lHindLow, D.rHindLow]) bone(i).rotation.x = 2.0;
  bone(D.neck).rotation.x = 0.7;
  bone(D.head).rotation.x = -0.3;
  body.position.y = 0.37;
}

type DogPoseFn = (bone: DogBoneOf) => void;

/** Leave the rig `k` of the way from pose `from` to pose `to`, per-bone slerp (as poseBlend.ts does for men). */
function blend(bones: readonly Bone[], bone: DogBoneOf, from: DogPoseFn, to: DogPoseFn, k: number): void {
  resetDog(bones);
  from(bone);
  bones.forEach((b, i) => {
    const snap = SNAP[i];
    if (!snap) return;
    snap.p.copy(b.position);
    snap.q.copy(b.quaternion);
  });
  resetDog(bones);
  to(bone);
  bones.forEach((b, i) => {
    const snap = SNAP[i];
    if (!snap) return;
    _p.copy(b.position);
    _q.copy(b.quaternion);
    b.position.lerpVectors(snap.p, _p, k);
    b.quaternion.slerpQuaternions(snap.q, _q, k);
  });
}

function death(bones: readonly Bone[], bone: DogBoneOf, age: number): void {
  const k = Math.min(1, Math.max(0, age / DOG_DEATH_SECONDS));
  if (k >= 1) {
    resetDog(bones);
    deadDog(bone);
  } else if (k < BUCKLE_AT) blend(bones, bone, stand, buckle, smooth(k / BUCKLE_AT));
  else {
    const over = smooth((k - BUCKLE_AT) / (1 - BUCKLE_AT));
    blend(bones, bone, buckle, deadDog, over);
    // Half way over, its legs are still under it: the body rides up on them rather than through the ground.
    bone(D.body).position.y += ROLL_LIFT * Math.sin(Math.PI * over);
  }
}

/** Pose the dog. `t` is the clock on the pose grid, seconds: it drives what a still dog does with its head. */
export function applyDogPose(bones: readonly Bone[], bone: DogBoneOf, input: DogPoseInput, t: number): void {
  resetDog(bones);
  switch (input.motion) {
    case 'dead':
      deadDog(bone);
      return;
    case 'death':
      death(bones, bone, input.age);
      return;
    case 'sit':
      sit(bone);
      break;
    case 'walk':
    case 'trot':
      gait(bone, input.travelled, input.motion === 'trot');
      break;
    case 'stand':
      stand(bone);
      break;
  }
  const still = input.motion === 'stand' || input.motion === 'sit';
  switch (input.head) {
    case 'sniff':
      // Standing, it sweeps on the clock; on the move, with the ground it covers. A trot runs the line.
      sniffHead(bone, still ? t : input.travelled, input.motion !== 'trot');
      break;
    case 'bark':
      barkHead(bone, input.sinceBark, input.motion === 'sit');
      break;
    case 'alert':
      alertHead(bone, t, still);
      break;
  }
}
